import React from 'react';
import { Body5, Container } from '@ui';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import styled from 'styled-components';
import { Working } from '@ui/Working';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { MultiSelect } from '@ui/MultiSelect';
import { GPT_5_MODEL_ID, resolveGptModelId } from '@constants';

const SearchInputWrapper = styled.div`
  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    min-height: 120px;
    padding: 16px;
    font-size: 14px;
    line-height: 18px;
  }
`;
const CompanySelectionWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content;
  width: max-content;
`;

const ResultWrapper = styled.div`
  display: inline-block;

  &:last-child {
    margin-bottom: 0px;
  }

  min-width: 440px;
  margin-bottom: 8px;
  margin-right: 16px;

  padding: 12px 16px;
  background: ${({ theme }) => convertHexToRGBA(theme.themeColors.lowContrastBorder, 1)};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.14s;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.05)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.2)};

    color: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.2)};

    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }

  svg {
    height: 18px;
    width: 18px;
    margin-right: 8px;
    margin-left: -4px;
  }
`;

const StepName = styled.div`
  * {
    opacity: 0.4;
  }

  &.active {
    * {
      opacity: 1;
    }
  }
`;
const ContinueButtonWrapper = styled.div`
  button {
    width: 300px;
  }
`;
const FormWrapper = styled.div`
  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-width: 420px;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const useSectorSelection = () => {
  const [results, setResults] = React.useState(null);
  const [isRunning, setIsRunning] = React.useState(false);

  const getResults = async (query) => {
    // const results = [
    //   {
    //     name: 'Adobe Inc.',
    //     ticker: 'ADBE',
    //     id: 99,
    //     description:
    //       'Adobe is a global software and cloud company providing creative tools, document services, and customer‑experience software, with AI (Firefly/Sensei) embedded across products.',
    //   },
    //   {
    //     name: 'Roblox Corporation',
    //     ticker: 'RBLX',
    //     id: 2983,
    //     description:
    //       'Roblox operates an online software platform for user‑generated, immersive experiences; it monetizes through virtual currency (Robux), subscriptions, and growing advertising.',
    //   },
    //   {
    //     name: 'Yelp Inc.',
    //     ticker: 'YELP',
    //     id: 3945,
    //     description:
    //       'Yelp is a local search and review software platform connecting consumers with local businesses, monetizing via advertising and lead‑generation products with AI‑driven features.',
    //   },
    // ];
    // setResults(results);
    // return;

    setResults(null);
    setIsRunning(true);

    const URL = `v1/genai/company_query`;
    const { status, data } = await sendApiRequest('post', URL, {
      question: query,
      model: resolveGptModelId(GPT_5_MODEL_ID),
    });
    handle400Statuses(status);
    logNetRequest(status, data);
    if (status === 200) {
      setResults(data);
    }
    setIsRunning(false);
  };

  const clearResults = () => {
    setResults(null);
  };

  return { results, isRunning, getResults, clearResults };
};

const CompanySelectionItem = ({ result, selectedCompanies, onSelect, onRemove }) => {
  const isSelected = selectedCompanies.some((c) => c.id === result.id);

  return (
    <ResultWrapper
      onClick={() => (isSelected ? onRemove(result) : onSelect(result))}
      className={isSelected ? 'selected' : ''}
    >
      <Body5>{result.name}</Body5>
    </ResultWrapper>
  );
};

const CompanySelection = ({ results, selectedCompanies, onSelectCompany, onRemoveCompany }) => {
  if (results === null || results.length === 0) {
    return (
      <Container vertical={24}>
        <Body5>No results found</Body5>
      </Container>
    );
  }
  return (
    <CompanySelectionWrapper>
      {results.map((result) => (
        <CompanySelectionItem
          key={result.id}
          result={result}
          selectedCompanies={selectedCompanies}
          onSelect={onSelectCompany}
          onRemove={onRemoveCompany}
        />
      ))}
    </CompanySelectionWrapper>
  );
};

const selectableDocumentTypes = [
  '10-K',
  '10-Q',
  '8-K',
  '20-F',
  '40-F',
  '6-K',
  'S-1',
  'Earnings Call Transcript',
].sort();

const last3YearsQuarters = getLastThreeYearsQuarters();
function getLastThreeYearsQuarters() {
  const now = new Date();
  const currentYear = now.getFullYear();
  // Determine the current quarter (1-4)
  const currentMonth = now.getMonth(); // 0-based
  const currentQuarter = Math.floor(currentMonth / 3) + 1;

  const quarters = [];
  for (let year = currentYear; year > currentYear - 3; year--) {
    let maxQuarter = 4;
    if (year === currentYear) {
      maxQuarter = currentQuarter;
    }
    for (let q = maxQuarter; q >= 1; q--) {
      quarters.push(`Q${q} ${year}`);
    }
  }
  return quarters;
}

const selectableTimePeriods = ['Most Recent', ...last3YearsQuarters];

function groupById(arr) {
  const groups = {};
  arr.forEach((item) => {
    const id = item.company_id;
    if (!groups[id]) {
      groups[id] = [];
    }
    groups[id].push(item);
  });
  return Object.values(groups);
}

function getMostRecentByReportDate(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr.reduce((latest, item) => {
    if (!latest) return item;
    if (item.report_date && latest.report_date) {
      return new Date(item.report_date) > new Date(latest.report_date) ? item : latest;
    }
    if (item.report_date) return item;
    return latest;
  }, null);
}

function getQuarterAndYearFromTimePeriod(tp) {
  const match = tp.match(/^Q([1-4]) (\d{4})$/);
  if (!match) return null;
  return { quarter: parseInt(match[1], 10), year: parseInt(match[2], 10) };
}

function getQuarterFromDate(dateStr) {
  // dateStr: 'YYYY-MM-DD'
  const date = new Date(dateStr);
  if (isNaN(date)) return null;
  const month = date.getMonth(); // 0-based
  return Math.floor(month / 3) + 1;
}

const matchingDocuments = (documentType, documents, timePeriod) => {
  const filteredDocuments = documents.filter((d) => d.doc_type === documentType);
  const matchingDocuments = [];
  if (timePeriod === 'Most Recent') {
    const mostRecent = getMostRecentByReportDate(filteredDocuments);
    if (mostRecent) {
      matchingDocuments.push(mostRecent);
    }
    return matchingDocuments;
  }

  const period = getQuarterAndYearFromTimePeriod(timePeriod);
  if (!period) return null;
  return filteredDocuments.filter((d) => {
    if (!d.report_date) return false;
    const date = new Date(d.report_date);
    if (isNaN(date)) return false;
    const year = date.getFullYear();
    const quarter = getQuarterFromDate(d.report_date);
    return year === period.year && quarter === period.quarter;
  });
};

const selectDocuments = (documentTypes, documents, timePeriod) => {
  const companyDocuments = groupById(documents);
  const groupedMatchingDocuments = [];
  companyDocuments.forEach((companyDocuments) => {
    const companyMatchingDocuments = [];
    documentTypes.forEach((documentType) => {
      const docTypeMatchingDocuments = matchingDocuments(documentType, companyDocuments, timePeriod);
      if (docTypeMatchingDocuments) {
        if (Array.isArray(docTypeMatchingDocuments)) {
          docTypeMatchingDocuments.forEach((d) => {
            companyMatchingDocuments.push(d);
          });
        } else {
          companyMatchingDocuments.push(docTypeMatchingDocuments);
        }
      }
    });
    if (companyMatchingDocuments.length > 0) {
      groupedMatchingDocuments.push(companyMatchingDocuments);
    }
  });
  return groupedMatchingDocuments;
};

export const SectorDocumentSelection = ({ handleAddDocuments, onDismiss }) => {
  const [step, setStep] = React.useState(1);

  const [selectedCompanies, setSelectedCompanies] = React.useState([]);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = React.useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = React.useState('Most Recent');
  const [query, setQuery] = React.useState('');
  const { results, isRunning, getResults, clearResults } = useSectorSelection();

  const [formError, setFormError] = React.useState(null);
  const [isGettingDocuments, setIsGettingDocuments] = React.useState(false);

  const onSubmit = () => {
    getResults(query);
  };

  const onClearSearchResults = () => {
    setQuery('');
    clearResults();
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const onSelectCompany = (company) => {
    setSelectedCompanies([...selectedCompanies, company]);
  };
  const onRemoveCompany = (company) => {
    setSelectedCompanies(selectedCompanies.filter((c) => c.id !== company.id));
  };

  const onContinueToDocTypeSelection = () => {
    setStep(2);
  };

  const onSelectDocuments = async () => {
    if (selectedDocumentTypes.length === 0) {
      setFormError('Please select a document type');
      return;
    }

    setIsGettingDocuments(true);
    const URL = `v1/genai/documents?category=company&company_ids=${selectedCompanies
      .map((c) => c.id)
      .join(',')}&doc_types=${selectedDocumentTypes.join(',')}`;
    const { status, data } = await sendApiRequest('get', URL);
    handle400Statuses(status);
    logNetRequest(status, data, URL);
    setIsGettingDocuments(false);
    if (status === 200) {
      const selectedDocuments = selectDocuments(selectedDocumentTypes, data.documents, selectedTimePeriod);
      if (selectedDocuments.length === 0) {
        setFormError('No documents found for the selected companies and document type');
        return;
      }

      handleAddDocuments(selectedDocuments);
    } else {
      setFormError('Something went wrong, please try again.');
      return;
    }
  };

  const onSelectDocumentType = (v) => {
    setSelectedDocumentTypes((prevState) => [...prevState, v]);
  };

  const onRemoveDocumentType = (v) => {
    setSelectedDocumentTypes((prevState) => prevState.filter((d) => d !== v));
  };

  const onSelectTimePeriod = (e) => {
    setSelectedTimePeriod(e.target.value);
  };

  const onSelectAll = () => {
    setSelectedCompanies(results);
  };

  const onUnselectAll = () => {
    setSelectedCompanies([]);
  };

  return (
    <Container>
      <Container bottom={40} row verticallyCenter>
        <StepName className={step === 1 ? 'active' : ''}>
          <Body5 bold>Select Companies</Body5>
        </StepName>
        <Container left={16} right={16} top={3}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            style={{ width: '16px', height: '16px', opacity: '0.2' }}
          >
            <path d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z" />
          </svg>
        </Container>
        <StepName className={step === 2 ? 'active' : ''}>
          <Body5 bold>Select Document Type</Body5>
        </StepName>
      </Container>

      {step === 1 && results === null && (
        <Container>
          {isRunning ? (
            <Container top={12} left={16}>
              <Working noVerticalPadding />
            </Container>
          ) : (
            <SearchInputWrapper>
              <Container bottom={16}>
                <Body5 bold>Enter a description to search for companies</Body5>
              </Container>
              <textarea type="text" placeholder="Enter a description..." value={query} onChange={handleQueryChange} />
              <Container top={24} style={{ textAlign: 'center' }}>
                <FlatButton fullWidth onClick={onSubmit}>
                  Search
                </FlatButton>
              </Container>
            </SearchInputWrapper>
          )}
        </Container>
      )}

      {results !== null && step === 1 && (
        <Container>
          <Body5 bold>Results for "{query}"</Body5>
          <Container top={24}>
            <ButtonWrapper>
              <SkeletonButton onClick={onSelectAll}>Select All</SkeletonButton>
              <SkeletonButton onClick={onUnselectAll}>Unselect All</SkeletonButton>
            </ButtonWrapper>
          </Container>
          <Container top={24}>
            <CompanySelection
              results={results}
              selectedCompanies={selectedCompanies}
              onSelectCompany={onSelectCompany}
              onRemoveCompany={onRemoveCompany}
            />
          </Container>
          <Container top={24} row verticallyCenter>
            <ContinueButtonWrapper>
              <FlatButton onClick={onContinueToDocTypeSelection} disabled={selectedCompanies.length === 0}>
                Continue
              </FlatButton>
            </ContinueButtonWrapper>
            <Container left={16}>
              <TransparentFlatButton fullWidth onClick={onClearSearchResults}>
                Clear
              </TransparentFlatButton>
            </Container>
          </Container>
        </Container>
      )}

      {step === 2 && (
        <Container>
          {isGettingDocuments ? (
            <Container>
              <Working noVerticalPadding />
            </Container>
          ) : (
            <Container>
              <Body5 bold>Selected Companies</Body5>
              <Container top={24} row>
                {selectedCompanies.map((c) => (
                  <Container key={c.id} right={16} bottom={8}>
                    <Body5>{c.name}</Body5>
                  </Container>
                ))}
              </Container>

              <FormWrapper>
                <Container top={24}>
                  <Container bottom={8}>
                    <Body5 bold>Select Time Period</Body5>
                  </Container>
                  <select value={selectedTimePeriod} onChange={onSelectTimePeriod}>
                    <option value="">Please select a time period</option>
                    {selectableTimePeriods.map((r, i) => (
                      <option key={i} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </Container>
                <Container>
                  <Container top={24}>
                    <Body5 bold>Select Document Types</Body5>
                    <Container top={8}>
                      <MultiSelect
                        values={selectedDocumentTypes}
                        items={selectableDocumentTypes}
                        onSelectItem={onSelectDocumentType}
                        onRemoveItem={onRemoveDocumentType}
                      />
                    </Container>
                  </Container>
                </Container>
              </FormWrapper>

              {formError && (
                <Container top={16}>
                  <Body5 error>{formError}</Body5>
                </Container>
              )}
              <Container top={36}>
                <FlatButton fullWidth onClick={onSelectDocuments}>
                  Add Documents
                </FlatButton>
              </Container>
            </Container>
          )}
        </Container>
      )}
    </Container>
  );
};
